import React from 'react'
import {
    BarChart,
    Bar,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Cell
} from 'recharts'
import { useQuery } from 'react-query'
import {
    useForm,
    Controller
} from 'react-hook-form'
import api from '../../../components/api'
export default function DashboardHistograma(props) {
    const {
        adesao_institucional_id,
        anos,
        modo: modoDashboard = 'RELATORIO',
        exportandoPDF = false
    } = props
    const anosSelecionados =
        Array.isArray(anos)
            ? [
                ...new Set(
                    anos
                        .map(item =>
                            Number(item)
                        )
                        .filter(item =>
                            Number.isInteger(item) &&
                            item > 0
                        )
                )
            ].sort(
                (a, b) =>
                    a - b
            )
            : []
    const anoSelecionado =
        anosSelecionados.length === 1
            ? anosSelecionados[0]
            : null
    // =========================================================
    // FORM
    // =========================================================
    const {
        control,
        watch,
        setValue
    } = useForm({
        defaultValues: {
            grupoResiduo_id: '',
            subGrupoResiduo_id: '',
            tipoTratamento_id: '',
            indicador: 'kg'
        }
    })
    // =========================================================
    // FILTROS
    // =========================================================
    const grupoResiduo_id =
        watch('grupoResiduo_id')
    const subGrupoResiduo_id =
        watch('subGrupoResiduo_id')
    const tipoTratamento_id =
        watch('tipoTratamento_id')
    const indicador =
        watch('indicador')
    // =========================================================
    // GRUPOS
    // =========================================================
    const {
        data: grupos = [],
        isLoading: loadingGrupos
    } = useQuery({
        queryKey: [
            'grupoResidHistograma'
        ],
        queryFn: async () => {
            const result =
                await api.get(
                    'grupoResid'
                )
            return result.data ?? []
        }
    })
    // =========================================================
    // SUBGRUPOS
    // =========================================================
    const {
        data: subgrupos = [],
        isLoading: loadingSubgrupos
    } = useQuery({
        queryKey: [
            'subGrupoResidHistograma',
            grupoResiduo_id
        ],
        queryFn: async () => {
            const result =
                await api.get(
                    `subGrupoResid/${grupoResiduo_id}`
                )
            return result.data ?? []
        },
        enabled:
            !!grupoResiduo_id
    })
    // =========================================================
    // TRATAMENTOS
    // =========================================================
    const {
        data: tratamentos = [],
        isLoading: loadingTratamentos
    } = useQuery(
        'tipoTratamentoTodosDashboard',
        async () => {
            const result =
                await api.get(
                    'tipoTratamentoTodos'
                )
            return result.data || []
        },
        {
            refetchOnWindowFocus: false,
            staleTime: 5 * 60 * 1000
        }
    )
    // =========================================================
    // DASHBOARD
    // =========================================================
    const {
        data,
        isLoading,
        isFetching,
        isError
    } = useQuery({
        queryKey: [
            'residuosDashboardHistograma',
            adesao_institucional_id,
            anosSelecionados.join(','),
            modoDashboard,
            grupoResiduo_id,
            subGrupoResiduo_id,
            tipoTratamento_id,
            indicador
        ],
        queryFn: async () => {
            const result =
                await api.post(
                    'residuosDashboardHistograma',
                    {
                        adesao_institucional_id:
                            Number(
                                adesao_institucional_id
                            ),
                        anos:
                            anosSelecionados,
                        modo:
                            modoDashboard,
                        grupoResiduo_id:
                            grupoResiduo_id
                                ? Number(
                                    grupoResiduo_id
                                )
                                : null,
                        subgrupoResiduo_id:
                            subGrupoResiduo_id
                                ? Number(
                                    subGrupoResiduo_id
                                )
                                : null,
                        tipoTratamento_id:
                            tipoTratamento_id
                                ? Number(
                                    tipoTratamento_id
                                )
                                : null,
                        indicador:
                            indicador || 'kg'
                    }
                )
            return result.data
        },
        enabled:
            !!adesao_institucional_id
    })
    // =========================================================
    // ALTERA GRUPO
    // =========================================================
    const alteraGrupo = (
field,
valor
    ) => {
field.onChange(valor)
        setValue(
            'subGrupoResiduo_id',
            ''
        )
    }
    // =========================================================
    // ALTERA SUBGRUPO
    // =========================================================
    const alteraSubgrupo = (
field,
valor
    ) => {
field.onChange(valor)
    }
    // =========================================================
    // FORMATADORES
    // =========================================================
    const formataNumero = (valor) => {
        if (
valor === null ||
valor === undefined
        ) {
            return '—'
        }
        return Number(
valor
        ).toLocaleString(
            'pt-BR',
            {
                minimumFractionDigits: 1,
                maximumFractionDigits: 2
            }
        )
    }
    // =========================================================
    // DADOS
    // =========================================================
    const dadosGrafico =
        (data?.dados ?? []).map(
item => ({
                ...item,
                possuiPendencia:
                    modoDashboard === 'CORRECAO' &&
item.possuiPendencia === true
            })
        )
    const maiorGeracao =
        data?.maiorGeracao ?? null
    const unidade =
        data?.unidade || 'kg'
    const descricaoIndicador =
        data?.descricaoIndicador || 'Quilograma'
    const indicadorSelecionado =
        data?.indicador || indicador || 'kg'
    const tipoCalculo =
        data?.tipoCalculo
    const ehAnoUnico =
        tipoCalculo === 'soma'
    const ehMediaSelecionados =
        tipoCalculo ===
        'media_anos_selecionados'
    const ehMediaHistorica =
        tipoCalculo ===
        'media_historica'
    const anoExibicao =
        data?.ano ??
        anoSelecionado
    const rotuloValor =
        ehMediaHistorica
            ? 'Média histórica'
            : ehMediaSelecionados
                ? 'Média dos anos selecionados'
                : indicadorSelecionado === 'kg'
                    ? 'Geração'
                    : descricaoIndicador
    const rotuloMaiorValor =
        ehAnoUnico
            ? indicadorSelecionado === 'kg'
                ? 'Mês de maior geração'
                : 'Mês de maior valor do indicador'
            : ehMediaSelecionados
                ? indicadorSelecionado === 'kg'
                    ? 'Mês de maior geração média'
                    : 'Mês de maior valor médio'
                : indicadorSelecionado === 'kg'
                    ? 'Mês de maior geração média histórica'
                    : 'Mês de maior valor médio histórico'
    // =========================================================
    // TOOLTIP
    // =========================================================
    const TooltipHistograma = ({
active,
payload,
label
    }) => {
        if (
            !active ||
            !payload ||
payload.length === 0
        ) {
            return null
        }
        const item =
payload[0]?.payload
        if (!item) {
            return null
        }
        return (
            <div
style={{
                    backgroundColor:
                        '#ffffff',
                    border:
                        '1px solid #d7dce1',
                    borderRadius:
                        '8px',
                    padding:
                        '12px 15px',
                    boxShadow:
                        '0 3px 12px rgba(0,0,0,0.12)',
                    minWidth:
                        '170px'
                }}
            >
                <div
style={{
                        fontWeight:
                            '700',
                        color:
                            '#333',
                        marginBottom:
                            '7px'
                    }}
                >
                    {item.nome || label}
                </div>
                <div
style={{
                        fontSize:
                            '13px',
                        color:
                            '#555'
                    }}
                >
                    {rotuloValor}:{' '}
                    <strong>
                        {formataNumero(
                            item.valor
                        )} {unidade}
                    </strong>
                </div>
                {
                    item.quantidadeAnos >
                        0 && (
                        <div
style={{
                                marginTop:
                                    '5px',
                                color:
                                    '#888',
                                fontSize:
                                    '11px'
                            }}
                        >
                            {
                                item.quantidadeAnos
                            } ano(s) considerado(s)
                        </div>
                    )
                }
                {item.possuiPendencia && (
                    <div
style={{
                            marginTop: '7px',
                            color: '#e67e22',
                            fontSize: '11px',
                            fontWeight: '600'
                        }}
                    >
                        <i
className="fa fa-exclamation-triangle"
style={{
                                marginRight: '5px'
                            }}
                        />
                        {ehAnoUnico
                            ? 'Contém dados com pendência'
                            : item.quantidadeAnosComPendencia > 0
                                ? `${item.quantidadeAnosComPendencia} ano(s) com pendência`
                                : 'Contém dados com pendência'
                        }
                    </div>
                )}
            </div>
        )
    }
    // =========================================================
    // LOADING
    // =========================================================
    if (isLoading) {
        return (
            <div
className="text-center"
style={{
                    padding:
                        '80px'
                }}
            >
                <i
className="fa fa-spinner fa-spin"
style={{
                        fontSize:
                            '30px'
                    }}
                />
                <div
style={{
                        marginTop:
                            '10px'
                    }}
                >
                    Carregando histograma...
                </div>
            </div>
        )
    }
    // =========================================================
    // ERRO
    // =========================================================
    if (isError) {
        return (
            <div className="alert alert-danger">
                Não foi possível carregar os dados do histograma.
            </div>
        )
    }
    return (
        <div>
            {/* =====================================================
                FILTROS
               ===================================================== */}
            <div
style={{
                    backgroundColor:
                        '#f8f9fa',
                    border:
                        '1px solid #e1e5e8',
                    borderRadius:
                        '12px',
                    padding:
                        '18px',
                    marginBottom:
                        '20px'
                }}
            >
                <div className="row">
                    {/* =============================================
                        GRUPO
                       ============================================= */}
                    <div className="col-md-3">
                        <div className="form-group">
                            <label>
                                Grupo de resíduo
                            </label>
                            <Controller
name="grupoResiduo_id"
control={control}
render={({ field }) => (
                                    <select
                                        {...field}
className="form-control"
disabled={
                                            loadingGrupos
                                        }
onChange={
e =>
                                                alteraGrupo(
                                                    field,
                                                    e.target.value
                                                )
                                        }
                                    >
                                        <option value="">
                                            Todos
                                        </option>
                                        {grupos.map(
rs => (
                                                <option
key={
                                                        rs.grupoResiduo_id
                                                    }
value={
                                                        rs.grupoResiduo_id
                                                    }
                                                >
                                                    {
                                                        rs.grupoResiduo_descri
                                                    }
                                                </option>
                                            )
                                        )}
                                    </select>
                                )}
                            />
                        </div>
                    </div>
                    {/* =============================================
                        SUBGRUPO
                       ============================================= */}
                    <div className="col-md-3">
                        <div className="form-group">
                            <label>
                                Subgrupo
                            </label>
                            <Controller
name="subGrupoResiduo_id"
control={control}
render={({ field }) => (
                                    <select
                                        {...field}
className="form-control"
disabled={
                                            !grupoResiduo_id ||
                                            loadingSubgrupos
                                        }
onChange={
e =>
                                                alteraSubgrupo(
                                                    field,
                                                    e.target.value
                                                )
                                        }
                                    >
                                        <option value="">
                                            {
                                                !grupoResiduo_id
                                                    ? 'Selecione o grupo'
                                                    : 'Todos'
                                            }
                                        </option>
                                        {subgrupos.map(
rs => (
                                                <option
key={
                                                        rs.subGrupoResiduo_id
                                                    }
value={
                                                        rs.subGrupoResiduo_id
                                                    }
                                                >
                                                    {
                                                        rs.subGrupoResiduo_descri
                                                    }
                                                </option>
                                            )
                                        )}
                                    </select>
                                )}
                            />
                        </div>
                    </div>
                    {/* =============================================
                        TRATAMENTO
                       ============================================= */}
                    <div className="col-md-3">
                        <div className="form-group">
                            <label>
                                Tratamento/Destinação
                            </label>
                            <Controller
name="tipoTratamento_id"
control={control}
render={({ field }) => (
                                    <select
                                        {...field}
className="form-control"
disabled={
                                            loadingTratamentos
                                        }
                                    >
                                        <option value="">
                                            Todos
                                        </option>
                                        {tratamentos.map(rs => (
                                            <option
key={
                                                    rs.tipoTratamento_id
                                                }
value={
                                                    rs.tipoTratamento_id
                                                }
                                            >
                                                {
                                                    rs.tipoTratamento_descri
                                                }
                                            </option>
                                        ))}
                                    </select>
                                )}
                            />
                        </div>
                    </div>
                    {/* =================================================
                        INDICADOR
                        ================================================= */}
                    <div className="col-md-3">
                        <div className="form-group">
                            <label>
                                Pesagem
                            </label>
                            <Controller
name="indicador"
control={control}
render={({ field }) => (
                                    <select
                                        {...field}
className="form-control"
                                    >
                                        <option value="kg">
                                            Quilograma (kg)
                                        </option>
                                        <option value="kg_fte">
                                            kg/FTE
                                        </option>
                                        <option value="kg_paciente_dia">
                                            kg/Paciente Dia
                                        </option>
                                        <option value="kg_area">
                                            kg/Área construída
                                        </option>
                                    </select>
                                )}
                            />
                        </div>
                    </div>
                </div>
            </div>
            {/* =====================================================
                CABEÇALHO
               ===================================================== */}
            <div
className="d-flex justify-content-between align-items-center"
style={{
                    marginBottom:
                        '15px'
                }}
            >
                <div>
                    <h4
style={{
                            margin: 0,
                            fontWeight: '600',
                            color: '#333'
                        }}
                    >
                        {indicadorSelecionado === 'kg'
                            ? 'Histograma geração mensal'
                            : `Histograma mensal - ${descricaoIndicador}`
                        }
                    </h4>
                    <div
style={{
                            fontSize: '13px',
                            color: '#777',
                            marginTop: '4px'
                        }}
                    >
                        {ehAnoUnico
                            ? indicadorSelecionado === 'kg'
                                ? `Geração mensal de resíduos em ${anoExibicao}`
                                : `${descricaoIndicador} mensal em ${anoExibicao}`
                            : ehMediaSelecionados
                                ? indicadorSelecionado === 'kg'
                                    ? `Média mensal de geração de resíduos em ${anosSelecionados.join(', ')}`
                                    : `Média mensal - ${descricaoIndicador} - ${anosSelecionados.join(', ')}`
                                : indicadorSelecionado === 'kg'
                                    ? 'Média histórica mensal de geração de resíduos'
                                    : `Média histórica mensal - ${descricaoIndicador}`
                        }
                    </div>
                </div>
                {isFetching && (
                    <div
style={{
                            fontSize:
                                '12px',
                            color:
                                '#777'
                        }}
                    >
                        <i
className="fa fa-spinner fa-spin"
style={{
                                marginRight:
                                    '5px'
                            }}
                        />
                        Atualizando...
                    </div>
                )}
            </div>
            {/* =====================================================
                GRÁFICO + PLACAR
               ===================================================== */}
            <div className="row">
                {/* =================================================
                    GRÁFICO
                   ================================================= */}
                <div className="col-md-9">
                    <div
style={{
                            height:
                                '430px',
                            backgroundColor:
                                '#ffffff',
                            border:
                                '1px solid #d7dce1',
                            borderRadius:
                                '14px',
                            padding:
                                '20px',
                            boxShadow:
                                '0 2px 8px rgba(0,0,0,0.04)'
                        }}
                    >
                        {
                            dadosGrafico.length === 0
                                ? (
                                    <div
style={{
                                            height:
                                                '100%',
                                            display:
                                                'flex',
                                            alignItems:
                                                'center',
                                            justifyContent:
                                                'center',
                                            color:
                                                '#777'
                                        }}
                                    >
                                        Nenhum dado disponível para os filtros selecionados.
                                    </div>
                                )
                                : (
                                    <ResponsiveContainer
width="100%"
height="100%"
                                    >
                                        <BarChart
data={
                                                dadosGrafico
                                            }
margin={{
                                                top:
                                                    20,
                                                right:
                                                    25,
                                                left:
                                                    20,
                                                bottom:
                                                    15
                                            }}
                                        >
                                            <defs>
                                                <pattern
id="histogramaPendencia"
width="8"
height="8"
patternUnits="userSpaceOnUse"
patternTransform="rotate(45)"
                                                >
                                                    <rect
width="8"
height="8"
fill="#fff4e8"
                                                    />
                                                    <line
x1="0"
y1="0"
x2="0"
y2="8"
stroke="#e67e22"
strokeWidth="3"
                                                    />
                                                </pattern>
                                            </defs>
                                            <CartesianGrid
strokeDasharray="3 3"
vertical={false}
                                            />
                                            <XAxis
dataKey="periodo"
                                            />
                                            <YAxis
tickFormatter={
value =>
                                                        Number(
                                                            value
                                                        ).toLocaleString(
                                                            'pt-BR'
                                                        )
                                                }
                                            />
                                            <Tooltip
content={
                                                    <TooltipHistograma />
                                                }
                                            />
                                            <Bar
dataKey="valor"
name={descricaoIndicador}
fill="#8bc5eb"
radius={[
                                                    6,
                                                    6,
                                                    0,
                                                    0
                                                ]}
maxBarSize={
                                                    55
                                                }
                                                isAnimationActive={!exportandoPDF}
                                            >
                                                {dadosGrafico.map(
item => (
                                                        <Cell
key={
                                                                item.ordem
                                                            }
fill={
                                                                item.possuiPendencia
                                                                    ? 'url(#histogramaPendencia)'
                                                                    : maiorGeracao &&
                                                                    item.ordem ===
                                                                    maiorGeracao.ordem
                                                                        ? '#1683c7'
                                                                        : '#8bc5eb'
                                                            }
                                                        />
                                                    )
                                                )}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                )
                        }
                    </div>
                </div>
                {/* =================================================
                    PLACAR
                   ================================================= */}
                <div className="col-md-3">
                    <div
style={{
                            minHeight:
                                '300px',
                            height:
                                '100%',
                            maxHeight:
                                '430px',
                            border:
                                '1px solid #1683c7',
                            borderRadius:
                                '14px',
                            backgroundColor:
                                '#eef8ff',
                            padding:
                                '30px 20px',
                            display:
                                'flex',
                            flexDirection:
                                'column',
                            alignItems:
                                'center',
                            justifyContent:
                                'center',
                            textAlign:
                                'center',
                            boxShadow:
                                '0 2px 8px rgba(0,0,0,0.04)'
                        }}
                    >
                        <div
style={{
                                color:
                                    '#1683c7',
                                fontSize:
                                    '18px',
                                fontWeight:
                                    '700',
                                lineHeight:
                                    '1.4',
                                marginBottom:
                                    '25px'
                            }}
                        >
                            {rotuloMaiorValor}
                        </div>
                        <div
style={{
                                color:
                                    '#222',
                                fontSize:
                                    '38px',
                                fontWeight:
                                    '700',
                                lineHeight:
                                    '1.1',
                                wordBreak:
                                    'break-word'
                            }}
                        >
                            {
                                maiorGeracao
                                    ? maiorGeracao.nome
                                    : '—'
                            }
                        </div>
                        {maiorGeracao && (
                            <div
style={{
                                    color:
                                        '#666',
                                    fontSize:
                                        '14px',
                                    marginTop:
                                        '20px'
                                }}
                            >
                                <strong>
                                    {formataNumero(
                                        maiorGeracao.valor
                                    )} {unidade}
                                </strong>
                                <div
style={{
                                        marginTop:
                                            '5px',
                                        fontSize:
                                            '12px',
                                        color:
                                            '#888'
                                    }}
                                >
                                    {ehAnoUnico
                                        ? indicadorSelecionado === 'kg'
                                            ? `Total em ${maiorGeracao.nome}`
                                            : `Valor em ${maiorGeracao.nome}`
                                        : ehMediaSelecionados
                                            ? indicadorSelecionado === 'kg'
                                                ? 'Média dos anos selecionados'
                                                : 'Média do indicador nos anos selecionados'
                                            : indicadorSelecionado === 'kg'
                                                ? 'Média histórica do mês'
                                                : 'Média histórica do indicador'
                                    }
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {/* =====================================================
                INFORMAÇÃO COMPLEMENTAR
               ===================================================== */}
            {data?.total !== null &&
                data?.total !== undefined && (
                <div
style={{
                        marginTop:
                            '12px',
                        color:
                            '#777',
                        fontSize:
                            '12px'
                    }}
                >
                    {indicadorSelecionado === 'kg'
                        ? `Total informado em ${anoExibicao}:`
                        : `${descricaoIndicador} em ${anoExibicao}:`
                    }{' '}
                    <strong>
                        {formataNumero(
                            data.total
                        )} {unidade}
                    </strong>
                </div>
            )}
        </div>
    )
}